import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, MapPin, Package, Video, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import {
  getVendorConsultationSettings,
  getClientConsultationWithVendor,
} from '../../services/consultationService';
import BookConsultationModal from './BookConsultationModal';
import {
  getBrandFromVendor,
  getBrandButtonStyle,
  getBrandCtaLabel,
  isBrandActive,
  darkenColor,
} from '../../utils/vendorBranding';

const formatConsultationDate = (date) =>
  date
    ? new Date(date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

const formatConsultationTime = (time) => {
  if (!time) return null;
  const parts = String(time).split(':');
  const h = parseInt(parts[0] || 0, 10);
  const m = parts[1] || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${m} ${ampm}`;
};

const VendorBookingWidget = ({ vendor, services, minPrice, isAuthenticated, brand: brandProp }) => {
  const queryClient = useQueryClient();
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const brand = brandProp || getBrandFromVendor(vendor);
  const brandActive = isBrandActive(brand);
  const primaryBtnStyle = getBrandButtonStyle(brand, 'primary');
  const outlineBtnStyle = getBrandButtonStyle(brand, 'outline');
  const ctaLabel = getBrandCtaLabel(brand, services.length > 0 ? 'View Services' : 'Contact Vendor');
  const vendorId = vendor?.id;
  const { data: consultationData } = useQuery({
    queryKey: ['vendorConsultationSettings', vendorId],
    queryFn: () => getVendorConsultationSettings(vendorId),
    enabled: !!vendorId,
  });
  const { data: activeConsultationRes } = useQuery({
    queryKey: ['clientConsultationWithVendor', vendorId],
    queryFn: () => getClientConsultationWithVendor(vendorId),
    enabled: !!vendorId && isAuthenticated,
  });
  const consultationEnabled = consultationData?.data?.consultation_enabled ?? false;
  const consultationQuestions = consultationData?.data?.consultation_questions ?? [];
  const activeConsultation = activeConsultationRes?.data ?? null;
  const cheapestService = services.length > 0 ? services.reduce((prev, current) => 
    (prev.base_price || 0) < (current.base_price || 0) ? prev : current
  ) : null;

  const vendorRating = Number(vendor?.average_rating) || 0;
  const vendorReviews = Number(vendor?.total_reviews) || 0;

  return (
    <div className="rounded-2xl border border-m-border-light bg-m-surface p-6 shadow-[0_12px_40px_rgba(0,0,0,0.06)]">
      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-medium tracking-tight text-m-ink">
            ${minPrice ? minPrice.toFixed(2) : cheapestService?.base_price || 'Contact'}
          </span>
          {services.length > 0 && minPrice ? (
            <span className="text-sm text-m-ink-muted">starting from</span>
          ) : null}
        </div>
      </div>

      {/* Service Summary */}
      {services.length > 0 ? (
        <div className="mb-6 space-y-3 rounded-xl border border-m-border-light bg-m-canvas-light p-4">
          <div className="flex items-center justify-between border-b border-m-border-light pb-3">
            <span className="text-sm text-m-ink-muted">Services</span>
            <div className="flex items-center gap-1">
              <Package className="h-4 w-4 text-m-ink-muted" />
              <span className="text-sm font-medium text-m-ink">
                {services.length} available
              </span>
            </div>
          </div>
          <div className="text-sm text-m-ink-muted">
            Browse services to view pricing and book
          </div>
        </div>
      ) : null}

      {/* CTA Button */}
      <Link
        to={cheapestService ? `/services/${cheapestService.id}` : '#'}
        className={`mb-3 block w-full rounded-full py-3.5 px-4 text-center text-sm font-medium transition-colors ${
          brandActive ? 'text-white' : 'bg-m-ink text-white hover:bg-[#1a1a1a]'
        }`}
        style={brandActive ? primaryBtnStyle : undefined}
        onMouseEnter={(e) => {
          if (brandActive) {
            e.currentTarget.style.backgroundColor = darkenColor(brand.primary_color);
          }
        }}
        onMouseLeave={(e) => {
          if (brandActive && primaryBtnStyle) {
            e.currentTarget.style.backgroundColor = primaryBtnStyle.backgroundColor;
          }
        }}
      >
        {ctaLabel}
      </Link>

      {consultationEnabled && activeConsultation?.status === 'pending' && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-5 w-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-950">Consultation request pending</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-900/90">
                You&apos;ve already requested a consultation with{' '}
                <span className="font-medium">{vendor?.business_name || 'this vendor'}</span>
                {activeConsultation.scheduled_date && (
                  <>
                    {' '}
                    for{' '}
                    <span className="font-medium">
                      {formatConsultationDate(activeConsultation.scheduled_date)}
                      {activeConsultation.scheduled_time
                        ? ` at ${formatConsultationTime(activeConsultation.scheduled_time)}`
                        : ''}
                    </span>
                  </>
                )}
                . We&apos;ll email you when they accept and share the meeting link.
              </p>
            </div>
          </div>
        </div>
      )}

      {consultationEnabled &&
        (activeConsultation?.status === 'accepted' ||
          activeConsultation?.status === 'meeting_scheduled') && (
        <div className="mb-3 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-emerald-950">Consultation confirmed</p>
              <p className="mt-1 text-xs leading-relaxed text-emerald-900/90">
                Your consultation is scheduled
                {activeConsultation.scheduled_date && (
                  <>
                    {' '}
                    for{' '}
                    <span className="font-medium">
                      {formatConsultationDate(activeConsultation.scheduled_date)}
                      {activeConsultation.scheduled_time
                        ? ` at ${formatConsultationTime(activeConsultation.scheduled_time)}`
                        : ''}
                    </span>
                  </>
                )}
                . Check your email for details.
              </p>
              {activeConsultation.google_meet_link && (
                <a
                  href={activeConsultation.google_meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Join meeting
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {consultationEnabled && !activeConsultation ? (
        <button
          type="button"
          onClick={() => {
            setShowConsultationModal(true);
          }}
          className={`mb-3 flex w-full items-center justify-center gap-2 rounded-full border-2 px-4 py-3 text-center text-sm font-medium transition-colors ${
            brandActive
              ? ''
              : 'border-m-ink text-m-ink hover:bg-m-canvas-light'
          }`}
          style={brandActive ? outlineBtnStyle : undefined}
        >
          <Video className="h-4 w-4" />
          Book consultation
        </button>
      ) : null}

      <p className="mb-6 text-center text-xs text-m-ink-muted" />

      <BookConsultationModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clientConsultationWithVendor', vendorId] });
        }}
        vendorId={vendorId}
        vendorName={vendor?.business_name}
        serviceId={null}
        questions={consultationQuestions}
      />

      {/* Details Section */}
      <div className="border-t border-m-border-light pt-6">
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-m-ink-muted">Starting price</span>
            <span className="font-medium text-m-ink">
              ${minPrice ? minPrice.toFixed(2) : 'Contact'}
            </span>
          </div>

          {services.length > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Services available</span>
              <div className="flex items-center gap-1">
                <Package className="h-4 w-4 text-m-ink-muted" />
                <span className="font-medium text-m-ink">{services.length}</span>
              </div>
            </div>
          ) : null}

          {vendorRating > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Rating</span>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-current text-m-ink" />
                <span className="font-medium text-m-ink">
                  {typeof vendorRating === 'number' && !isNaN(vendorRating)
                    ? vendorRating.toFixed(1)
                    : '0.0'}
                  {vendorReviews > 0 ? (
                    <span className="ml-1 text-m-ink-muted">({vendorReviews})</span>
                  ) : null}
                </span>
              </div>
            </div>
          ) : null}

          {vendor?.city && vendor?.state ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Location</span>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4 text-m-ink-muted" />
                <span className="font-medium text-m-ink">
                  {vendor.city}, {vendor.state}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Report Listing */}
      {/* <div className="pt-6 border-t border-gray-200">
        <button className="w-full text-sm font-light text-black underline text-center hover:text-gray-600 transition-colors">
          Report this listing
        </button>
      </div> */}
    </div>
  );
};

export default VendorBookingWidget;

